import { useState } from 'react'

import { FilterStatuses } from './components/Filters'
import { ErrorsByField, type ReducerState, Row } from './editableContentReducer'

interface Props {
  stepData: ReducerState
  rows?: Array<Row | undefined>
}

const filteredRowsWithError = (stepData: ReducerState, index: number, row: Row | undefined) => {
  return stepData.rowsWithError?.includes(index) ? row : undefined
}

const filteredRowsWithCellError = (
  stepData: ReducerState,
  index: number,
  fieldName: keyof ErrorsByField,
  row?: Row
) => {
  return stepData.rowsByFieldErrors?.[fieldName]?.includes(index) ? row : undefined
}

const filterByStatus = {
  error: filteredRowsWithError,
  missingName: (stepData: ReducerState, index: number, row?: Row) =>
    filteredRowsWithCellError(stepData, index, 'first_name', row),
  missingLastName: (stepData: ReducerState, index: number, row?: Row) =>
    filteredRowsWithCellError(stepData, index, 'last_name', row),
  invalidEmail: (stepData: ReducerState, index: number, row?: Row) =>
    filteredRowsWithCellError(stepData, index, 'email', row),
  unrecognizedLegalEntity: (stepData: ReducerState, index: number, row?: Row) =>
    filteredRowsWithCellError(stepData, index, 'legal_entity_id', row),
  unrecognizedWorkplace: (stepData: ReducerState, index: number, row?: Row) =>
    filteredRowsWithCellError(stepData, index, 'location_id', row),
  unrecognizedTeams: (stepData: ReducerState, index: number, row?: Row) =>
    filteredRowsWithCellError(stepData, index, 'team_id', row),
  invalidSeniorityCalculationDate: (stepData: ReducerState, index: number, row?: Row) =>
    filteredRowsWithCellError(stepData, index, 'seniority_calculation_date', row),
  invalidContractStartDate: (stepData: ReducerState, index: number, row?: Row) =>
    filteredRowsWithCellError(stepData, index, 'contract_start_date', row),
  unrecognizedJobLevel: (stepData: ReducerState, index: number, row?: Row) =>
    filteredRowsWithCellError(stepData, index, 'job_catalog_level_id', row),
  unrecognizedNationality: (stepData: ReducerState, index: number, row?: Row) =>
    filteredRowsWithCellError(stepData, index, 'nationality', row),
  selectedRows: (_stepData: ReducerState, _index: number, row?: Row) =>
    row && row.selected ? row : undefined,
  ignoredRows: (_stepData: ReducerState, _index: number, row?: Row) =>
    row && !row.selected ? row : undefined,
  invalidPostalCode: (stepData: ReducerState, index: number, row?: Row) =>
    filteredRowsWithCellError(stepData, index, 'postal_code', row),
  invalidBirthdayOn: (stepData: ReducerState, index: number, row?: Row) =>
    filteredRowsWithCellError(stepData, index, 'birthday_on', row),
  invalidContactName: (stepData: ReducerState, index: number, row?: Row) =>
    filteredRowsWithCellError(stepData, index, 'contact_name', row),
  invalidContactNumber: (stepData: ReducerState, index: number, row?: Row) =>
    filteredRowsWithCellError(stepData, index, 'contact_number', row),
  invalidPhoneNumber: (stepData: ReducerState, index: number, row?: Row) =>
    filteredRowsWithCellError(stepData, index, 'phone_number', row),
  invalidAddressLine1: (stepData: ReducerState, index: number, row?: Row) =>
    filteredRowsWithCellError(stepData, index, 'address_line1', row),
  invalidCity: (stepData: ReducerState, index: number, row?: Row) =>
    filteredRowsWithCellError(stepData, index, 'city', row),
  invalidState: (stepData: ReducerState, index: number, row?: Row) =>
    filteredRowsWithCellError(stepData, index, 'state', row),
  unrecognizedCountry: (stepData: ReducerState, index: number, row?: Row) =>
    filteredRowsWithCellError(stepData, index, 'country', row),
  invalidA3InnuvaCode: (stepData: ReducerState, index: number, row?: Row) =>
    filteredRowsWithCellError(stepData, index, 'a3_innuva_code', row),
  invalidA3NomCode: (stepData: ReducerState, index: number, row?: Row) =>
    filteredRowsWithCellError(stepData, index, 'a3_nom_code', row),
  invalidSalaryAmountInCents: (stepData: ReducerState, index: number, row?: Row) =>
    filteredRowsWithCellError(stepData, index, 'salary_amount_in_cents', row),
  invalidEndsOnDate: (stepData: ReducerState, index: number, row?: Row) =>
    filteredRowsWithCellError(stepData, index, 'ends_on', row),
  invalidWorkingHours: (stepData: ReducerState, index: number, row?: Row) =>
    filteredRowsWithCellError(stepData, index, 'working_hours', row),
}

const useTableFilters = ({ stepData, rows }: Props) => {
  const [rowFilterStatus, setRowFilterStatus] = useState<FilterStatuses>()

  const rowsFilteredByStatus = rows?.map((row, index) => {
    if (!rowFilterStatus) return row

    return filterByStatus[rowFilterStatus](stepData, index, row)
  })

  const filtersCount = {
    error: stepData.rowsWithError?.length,
    missingName: stepData.rowsByFieldErrors?.first_name?.length,
    missingLastName: stepData.rowsByFieldErrors?.last_name?.length,
    invalidEmail: stepData.rowsByFieldErrors?.email?.length,
    invalidSeniorityCalculationDate: stepData.rowsByFieldErrors?.seniority_calculation_date?.length,
    unrecognizedLegalEntity: stepData.rowsByFieldErrors?.legal_entity_id?.length,
    unrecognizedWorkplace: stepData.rowsByFieldErrors?.location_id?.length,
    unrecognizedTeams: stepData.rowsByFieldErrors?.team_id?.length,
    unrecognizedNationality: stepData.rowsByFieldErrors?.nationality?.length,
    invalidContractStartDate: stepData.rowsByFieldErrors?.contract_start_date?.length,
    unrecognizedJobLevel: stepData.rowsByFieldErrors?.job_catalog_level_id?.length,
    selectedRows: stepData.rows?.filter((row) => row && row.selected)?.length || 0,
    ignoredRows: stepData.rows?.filter((row) => row && !row.selected)?.length || 0,
    invalidPostalCode: stepData.rowsByFieldErrors?.postal_code?.length,
    invalidBirthdayOn: stepData.rowsByFieldErrors?.birthday_on?.length,
    invalidContactName: stepData.rowsByFieldErrors?.contact_name?.length,
    invalidContactNumber: stepData.rowsByFieldErrors?.contact_number?.length,
    invalidPhoneNumber: stepData.rowsByFieldErrors?.phone_number?.length,
    invalidAddressLine1: stepData.rowsByFieldErrors?.address_line1?.length,
    invalidCity: stepData.rowsByFieldErrors?.city?.length,
    invalidState: stepData.rowsByFieldErrors?.state?.length,
    unrecognizedCountry: stepData.rowsByFieldErrors?.country?.length,
    invalidA3InnuvaCode: stepData.rowsByFieldErrors?.a3_innuva_code?.length,
    invalidA3NomCode: stepData.rowsByFieldErrors?.a3_nom_code?.length,
    invalidSalaryAmountInCents: stepData.rowsByFieldErrors?.salary_amount_in_cents?.length,
    invalidEndsOnDate: stepData.rowsByFieldErrors?.ends_on?.length,
    invalidWorkingHours: stepData.rowsByFieldErrors?.working_hours?.length,
  }

  const setRowFilter = (status: FilterStatuses) => {
    setRowFilterStatus(status)
  }

  return {
    rowsFiltered: rowsFilteredByStatus,
    setRowFilter,
    setRowFilterStatus,
    rowFilterStatus,
    filtersCount,
  }
}

export default useTableFilters
